import { FoundationColors } from './foundation-colors';

/*
 * Overrides appended AFTER the aura TW_PRO theme css (src/aura/theme), inside the same
 * Emotion <Global>, so they win on source order at equal specificity.
 * Coupled to aura's `--aura-blue-*` var names and `.aura--button-*` class names; the
 * aura-contract spec fails CI if a change to src/aura breaks those assumptions.
 */
const { blue, grey, white } = FoundationColors;

const PRIMARY = '.aura--button-primary';
const SECONDARY = '.aura--button-secondary:not(.aura--button-disruptive)';
const TERTIARY = '.aura--button-tertiary:not(.aura--button-disruptive)';
// aura's IconButton reuses `aura--button-tertiary` (plus `aura--icon-button-sm/md`). User decision
// (CPG-1020): keep IconButton's hover/pressed as today, i.e. no tinted square behind the password eye.
const TERTIARY_NO_ICON_BUTTON = `${TERTIARY}:not([class*="aura--icon-button"])`;

const rootRemap = `
:root {
  --aura-blue-700: ${blue[700]};
  --aura-blue-600: ${blue[600]};
  --aura-blue-400: ${blue[400]};
  --aura-blue-200: ${blue[200]};
  --aura-blue-100: ${blue[100]};
}`;

// Disruptive primary keeps aura's coral rules: aura's `.primary.disruptive:hover` is (0,3,0) and beats these (0,2,0).
const primaryButton = `
${PRIMARY}:hover { background-color: ${blue[700]}; }
${PRIMARY}:active { background-color: ${blue[800]}; }
${PRIMARY}:disabled {
  background-color: ${grey[300]} !important;
  color: ${grey[500]} !important;
  fill: ${grey[500]} !important;
}`;

// Reconnect `_button.css` secondary. Border changes via border-color only, so the 1px width is kept.
const secondaryButton = `
${SECONDARY} {
  background-color: ${white};
  color: ${blue[600]};
  fill: ${blue[600]};
  border-color: ${blue[600]};
}
${SECONDARY}:hover {
  background-color: ${blue[100]};
  color: ${blue[600]};
  fill: ${blue[600]};
  border-color: transparent;
}
${SECONDARY}:active {
  background-color: ${blue[100]};
  color: ${blue[800]};
  fill: ${blue[800]};
  border-color: transparent;
}
${SECONDARY}:disabled {
  background-color: ${white} !important;
  color: ${grey[800]} !important;
  fill: ${grey[800]} !important;
  border-color: ${grey[800]} !important;
}`;

// Reconnect `_button.css` tertiary. aura's `border: none` is kept (a transparent border would add 2px).
const tertiaryButton = `
${TERTIARY} {
  background-color: transparent;
  color: ${blue[600]};
  fill: ${blue[600]};
}
${TERTIARY_NO_ICON_BUTTON}:hover {
  background-color: ${blue[100]};
  color: ${blue[700]};
  fill: ${blue[700]};
}
${TERTIARY_NO_ICON_BUTTON}:active {
  background-color: ${blue[200]};
  color: ${blue[800]};
  fill: ${blue[800]};
}
${TERTIARY}:disabled {
  background-color: transparent !important;
  color: ${grey[800]} !important;
  fill: ${grey[800]} !important;
}`;

export const LOADER_CLASS_NAME = 'ssu-loader';
// aura's LoaderPro hard-codes this fill as an SVG presentation attribute, which any CSS rule beats.
// Fails safe: if the hex changes, the spinner renders aura's own colour.
export const AURA_LOADER_ARC_FILL = '#4036ED';

const loaderArc = `
.${LOADER_CLASS_NAME} svg path[fill="${AURA_LOADER_ARC_FILL}" i] { fill: ${blue[600]}; }`;

export const foundationOverridesCss = [
  rootRemap,
  primaryButton,
  secondaryButton,
  tertiaryButton,
  loaderArc,
].join('\n');

// Must stay in the SAME <Global> as theme.css: a separate sheet would depend on JSX render order.
export function withFoundationOverrides(themeCss: string): string {
  return `${themeCss}\n${foundationOverridesCss}`;
}
